import React, { useEffect, useState } from 'react';

import { Divider as AntDivider } from 'antd';
import classNames from 'classnames';
import styled from 'styled-components';

import Header from 'common/Header';
import { useAppSelector } from 'store/hook';
import { RecommendationCommunityOverviewType } from 'types/recommendations';

import { OVERALL_VIEW_ID } from './constants';
import RecommendationBreadcrumb from './RecommendationBreadcrumb';
import RecommendationCategoryTag from './RecommendationCategoryTag';
import RecommendationCommunityList from './RecommendationCommunityList';
import RecommendationCompany from './RecommendationCompany';
import useRecommendationDetailsContext from './RecommendationDetailsContext';
import RecommendationDetailsSidebar from './RecommendationDetailsSidebar';
import useRecommendationsContext from './RecommendationsContext';

const Container = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  width: 100%;
  overflow-y: auto;
`;

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  margin-right: 10px;
  padding: 1px;
  background-image: linear-gradient(var(--white), var(--white)),
    linear-gradient(to bottom, var(--competitor-purple), var(--negative-orange) 50%, var(--flamingo-pink));
  background-origin: border-box;
  background-clip: content-box, border-box;
`;

const Layout = styled.div`
  display: flex;
  flex-direction: row;
  border-top: 1px solid var(--line-gray);
  height: calc(100dvh - 130px);
`;

const Content = styled.div`
  border-left: 1px solid var(--line-gray);
  padding: 30px 47px;
  flex: 1 1 auto;
  min-width: 0;
  overflow-y: auto;

  &.communityView {
    padding: 30px 216px;
  }
`;

const TitleContainer = styled.div`
  font-size: 28px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-bold);
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px 30px;
`;

const CategoryTagContainer = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

const Title = styled.div`
  font-size: 22px;
  font-family: var(--font-bold);
  color: var(--text-primary);
  padding-left: 30px;
  padding-top: 10px;
`;

const RecommendationDetails = () => {
  const { company } = useRecommendationsContext();
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const { recommendation, filters, selectedItem } = useRecommendationDetailsContext();
  const [communityOverviews, setCommunityOverviews] = useState<RecommendationCommunityOverviewType[]>();

  const showProblemSolution = !selectedCommunity && selectedItem === OVERALL_VIEW_ID;

  useEffect(() => {
    if (!recommendation) {
      return;
    }

    if (filters.communities.length !== 0) {
      setCommunityOverviews(
        recommendation.community_overviews.filter((co) => filters.communities.includes(co.community_id))
      );
    } else {
      setCommunityOverviews(recommendation.community_overviews);
    }
  }, [recommendation, filters]);

  const categories = [
    ...recommendation.config.data_points.reduce((acc, dp) => {
      if (dp.category) {
        acc.add(dp.category);
      }
      return acc;
    }, new Set<string>()),
  ];

  return (
    <Container>
      <Header
        title={
          <TitleContainer>
            {recommendation.title}
            <CategoryTagContainer>
              {categories.map((c, index) => (
                <RecommendationCategoryTag key={index} category={c} />
              ))}
            </CategoryTagContainer>
          </TitleContainer>
        }
        breadcrumbs={<RecommendationBreadcrumb company={company.name} />}
        iconComponent={
          <IconContainer>
            <img src="/icons/ai-blob.svg" alt="ai-blob" height="30px" width="30px" />
          </IconContainer>
        }
      />
      <Layout>
        {!selectedCommunity && <RecommendationDetailsSidebar />}
        <Content
          className={classNames('mini-scroll', {
            communityView: selectedCommunity,
          })}
        >
          {showProblemSolution && <RecommendationCompany recommendation={recommendation} />}
          {!showProblemSolution && (
            <Main>
              <RecommendationCommunityList data={communityOverviews} />
            </Main>
          )}
        </Content>
      </Layout>
    </Container>
  );
};

export default RecommendationDetails;
